// Native IndexedDB stores only user-authored drafts/outbox payloads, never chat history.
export const DRAFT_TTL = 7 * 86400000;
export const OUTBOX_TTL = 86400000;
export function createLocalMessagingStore(factory = window.indexedDB) {
  let opening;
  const open = () => {
    if (!factory) return Promise.reject(new Error('Local storage is unavailable. Your message was not queued.'));
    if (!opening) opening = new Promise((resolve, reject) => {
      const request = factory.open('srm-outbox', 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('records')) request.result.createObjectStore('records', { keyPath: 'key' });
      };
      request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); opening = null; }; resolve(request.result); };
      request.onerror = () => { opening = null; reject(request.error); };
      request.onblocked = () => { opening = null; reject(new Error('Local storage is blocked by another tab.')); };
    });
    return opening;
  };
  async function transaction(mode, work) {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('records', mode);
      let value;
      const request = work(tx.objectStore('records'));
      if (request) request.onsuccess = () => { value = request.result; };
      tx.oncomplete = () => resolve(value);
      tx.onerror = tx.onabort = () => reject(tx.error || new Error('Local storage operation failed.'));
    });
  }
  const key = (owner, kind, id) => JSON.stringify([String(owner), kind, id]);
  return {
    put: record => transaction('readwrite', store => store.put({ ...record, key: key(record.user_id, record.store_kind, record.id) })),
    async putLatest(record) {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('records', 'readwrite'), store = tx.objectStore('records');
        const next = { ...record, key: key(record.user_id, record.store_kind, record.id) };
        const request = store.get(next.key);
        request.onsuccess = () => {
          const previous = request.result;
          if (!previous || next.edited_at > previous.edited_at ||
            (next.edited_at === previous.edited_at && `${next.writer}:${next.revision}` > `${previous.writer}:${previous.revision}`)) store.put(next);
        };
        tx.oncomplete = () => resolve();
        tx.onerror = tx.onabort = () => reject(tx.error || new Error('Draft storage failed.'));
      });
    },
    remove: (owner, kind, id) => transaction('readwrite', store => store.delete(key(owner, kind, id))),
    get: (owner, kind, id) => transaction('readonly', store => store.get(key(owner, kind, id))),
    async list(owner, kind) {
      const rows = await transaction('readonly', store => store.getAll());
      return (rows || []).filter(row => String(row.user_id) === String(owner) && (!kind || row.store_kind === kind));
    },
    async clearOwner(owner) {
      const rows = await this.list(owner);
      await Promise.all(rows.map(row => this.remove(owner, row.store_kind, row.id)));
    },
  };
}
export const localMessagingStore = createLocalMessagingStore();
